import type { GemmetricLiteScanResult, OrganizationKey } from './organizations';

export type { OrganizationKey, GemmetricLiteScanResult } from './organizations';

export const CATEGORY_GROUPS = [
  'agency_services',
  'legal',
  'dental',
  'medical',
  'vision',
  'hearing',
  'insurance',
  'financial',
  'automotive',
  'furniture',
  'mattress',
  'home_services',
  'hvac',
  'plumbing',
  'electrical',
  'roofing',
  'flooring',
  'beauty_wellness',
  'fitness',
  'education_childcare',
  'senior_living'
] as const;

export type CategoryGroup = typeof CATEGORY_GROUPS[number];

export const CATEGORY_GROUP_LABELS: Record<CategoryGroup, string> = {
  agency_services: 'Marketing & Digital Agencies',
  legal: 'Legal',
  dental: 'Dental',
  medical: 'Medical',
  vision: 'Vision',
  hearing: 'Hearing',
  insurance: 'Insurance',
  financial: 'Financial',
  automotive: 'Automotive',
  furniture: 'Furniture',
  mattress: 'Mattress',
  home_services: 'Home Services',
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  roofing: 'Roofing',
  flooring: 'Flooring',
  beauty_wellness: 'Beauty/Wellness',
  fitness: 'Fitness',
  education_childcare: 'Education/Childcare',
  senior_living: 'Senior Living'
};

export type ProspectStatus = 'saved' | 'working' | 'won' | 'lost';

export type ContactRole =
  | 'receptionist'
  | 'manager'
  | 'owner'
  | 'marketing_manager'
  | 'other';

export type CallType =
  | 'cold_call_phone'
  | 'cold_call_face_to_face'
  | 'follow_up_phone'
  | 'follow_up_face_to_face'
  | 'renewal'
  | 'lead'
  | 'servicing'
  | 'appointment'
  | 'email';

export type CallResponse =
  | 'sale'
  | 'call_back'
  | 'rejection'
  | 'new_appointment'
  | 'follow_up_appointment'
  | 'decision_maker_spoken_to';

export type AppointmentType = 'phone' | 'in_person' | 'virtual';

export interface AppointmentLogFields {
  appointmentAt: string; // ISO date-time
  durationMinutes?: number;
  appointmentType?: AppointmentType;
  appointmentLocation?: string;
  appointmentContactName?: string;
  appointmentContactPhone?: string;
  appointmentContactEmail?: string;
}

export interface AppointmentRecord extends AppointmentLogFields {
  id: string;
  userId: string;
  orgKey?: OrganizationKey;
  prospectId: string;
  name: string;
  category: string;
  phone: string;
  website: string;
  address: string;
  note?: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
  source?: 'manual' | 'ai_call' | 'call_log';
  callLogId?: string;
  outboundCallId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProspectRecord {
  id: string; // placeId preferred, else businessHash
  userId: string;
  orgKey?: OrganizationKey;

  placeId?: string;
  businessHash?: string;

  name: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;

  categoryGroup?: CategoryGroup;
  categoryLabel?: string;

  phone?: string;
  website?: string;
  phoneE164?: string;

  saved: boolean;
  status: ProspectStatus;
  bestContactId?: string;
  dnc?: boolean;
  dncReason?: string;
  dncAt?: string;
  attemptCount?: number;
  lastAttemptAt?: string;
  nextActionAt?: string;
  nextActionType?: 'call_back' | 'send_link' | 'none';
  ownerName?: string;
  ownerEmail?: string;

  lastActivityAt?: string;
  nextReminderAt?: string;
  nextAppointmentAt?: string;
  targetQualityScore?: 1 | 2 | 3 | 4 | 5;
  lastOutcome?: string;
  lastOutcomeAt?: string;
  callbackPending?: boolean;
  gemmetricLiteScan?: GemmetricLiteScanResult;
  orgInsights?: {
    gemmetricCandidateScore?: number;
    gemmetricLiteScan?: GemmetricLiteScanResult;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProspectContactRecord {
  id: string;
  userId: string;
  orgKey?: OrganizationKey;
  prospectId: string;
  name?: string;
  role: ContactRole;
  isDecisionMaker: boolean;
  phone?: string;
  email?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CallSheetRecord {
  id: string;
  userId: string;
  orgKey?: OrganizationKey;
  name: string;
  dateKey: string; // YYYY-MM-DD
  createdAt: string;
  updatedAt: string;
}

export interface CallSheetItemRecord {
  id: string;
  userId: string;
  orgKey?: OrganizationKey;
  sheetId: string;
  prospectId: string;
  order: number;
  addedAt: string;
  lastOutcome?: string;
  lastCalledAt?: string;
  queueState?: 'uncalled' | 'called' | 'callback' | 'rejected' | 'appointment';
}

export type OutboundCallStatus = 'queued' | 'dialing' | 'in_progress' | 'completed' | 'failed';

export type OutboundCallOutcome =
  | 'no_answer'
  | 'voicemail'
  | 'gatekeeper'
  | 'called'
  | 'dm_reached'
  | 'booked'
  | 'follow_up'
  | 'rejected'
  | 'dnc'
  | 'wrong_number'
  | 'failed';

export interface OutboundCallRecord {
  id: string;
  userId: string;
  orgKey?: OrganizationKey;
  callSheetId: string;
  prospectId: string;
  callSheetItemId?: string;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  status: OutboundCallStatus;
  outcome?: OutboundCallOutcome;
  providerOutcome?: string;
  callMode?: 'ai' | 'manual';
  transport?: 'elevenlabs' | 'twilio_browser';
  summary?: string;
  transcript?: string;
  recordingUrl?: string;
  elevenCallId?: string;
  callSid?: string;
  agentId?: string;
  toNumber?: string;
  error?: {
    code?: string;
    message?: string;
    raw?: string;
  };
  extracted?: {
    dmName?: string;
    dmRole?: string;
    email?: string;
    phone?: string;
    bestCallbackTime?: string;
    meetingTimeConfirmed?: string;
    proposedMeetingTime?: string;
  };
  idempotencyKey?: string;
  sequenceId?: string;
}

export const isLikelyGooglePlaceId = (id?: string | null): boolean => {
  if (!id) return false;
  return !(
    id.startsWith('osm_') ||
    id.startsWith('yelp_') ||
    id.startsWith('mock_') ||
    id.startsWith('prospect_')
  );
};

export const formatDateKey = (d: Date = new Date()): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const startOfWeekMonday = (d: Date = new Date()): Date => {
  const copy = new Date(d);
  copy.setHours(12, 0, 0, 0);
  const day = copy.getDay(); // 0 = Sun, 1 = Mon
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
};

export const formatWeekOfLabel = (d: Date = new Date()): string => {
  return `Week of ${formatMdy(startOfWeekMonday(d))}`;
};

export const nowIso = (): string => new Date().toISOString();

export const formatMdy = (d: Date): string => {
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

export const formatMdyTime = (d: Date): string => {
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const formatDateKeyMdy = (dateKey: string): string => {
  // dateKey is stored as YYYY-MM-DD
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!m) return dateKey;
  return `${m[2]}/${m[3]}/${m[1]}`;
};
